import { IconName } from "@fortawesome/pro-regular-svg-icons";

import { Optional } from "common/utils/types";

import { PatientKind } from ".";

/** Matches scheduler AppointmentStatus */
export enum AppointmentStatus {
  Creating = "CREATING",
  ClientCreating = "CLIENT_CREATING",
  Unscheduled = "UNSCHEDULED",
  Unconfirmed = "UNCONFIRMED",
  Confirmed = "CONFIRMED",
  DroppedOff = "DROPPED_OFF",
  CheckedIn = "CHECKED_IN",
  InProgress = "IN_PROGRESS",
  ReadyToGo = "READY_TO_GO",
  Completed = "COMPLETED",
  NoShow = "NO_SHOW",
  Canceled = "CANCELED",
  Imported = "IMPORTED",
  ClientRequested = "CLIENT_REQUESTED",
}

/**
 * Statuses that denote the patient is at the clinic.
 *
 * **Ordering here matters** - it follows the order they would
 * normally move through in the clinic.
 * */
export const IN_CLINIC_APPOINTMENT_STATUSES = [
  AppointmentStatus.DroppedOff,
  AppointmentStatus.CheckedIn,
  AppointmentStatus.InProgress,
  AppointmentStatus.ReadyToGo,
];

export const SHOW_TIMER_APPOINTMENT_STATUSES = [
  ...IN_CLINIC_APPOINTMENT_STATUSES,
  AppointmentStatus.Completed,
];

/** Matches AppointmentKindSlugs */
export enum AppointmentKindSlug {
  ExamProgress = "EXAM_PROGRESS",
  ExamProgressExotic = "EXAM_PROGRESS_EXOTIC",
  ExamInjuryIllness = "EXAM_INJURY_ILLNESS",
  ExamInjuryIllnessExotic = "EXAM_INJURY_ILLNESS_EXOTIC",
  ExamUrgent = "EXAM_URGENT",
  ExamUrgentExotic = "EXAM_URGENT_EXOTIC",
  ExamWellness = "EXAM_WELLNESS",
  ExamWellnessExotic = "EXAM_WELLNESS_EXOTIC",
  ExamNewPatient = "EXAM_NEW_PATIENT",
  ProcedureSedated = "PROCEDURE_SEDATED",
  ProcedureHolistic = "PROCEDURE_HOLISTIC",
  ProcedureDiagnostics = "PROCEDURE_DIAGNOSTICS",
  SurgerySoftTissue = "SURGERY_SOFT_TISSUE",
  SurgerySpay = "SURGERY_SPAY",
  SurgeryNeuter = "SURGERY_NEUTER",
  SurgeryOrthoPedic = "SURGERY_ORTHOPEDIC",
  SurgeryOther = "SURGERY_OTHER",
  TelemedicineGeneral = "TELEMEDICINE_GENERAL",
  TelemedicineProgress = "TELEMEDICINE_PROGRESS",
  TechnicianVisit = "TECHNICIAN_VISIT",
  Dental = "DENTAL",
  Procedure = "PROCEDURE",
  Euthanasia = "EUTHANASIA",
  DeadOnArrival = "DEAD_ON_ARRIVAL",
  Grooming = "GROOMING",
  Custom = "CUSTOM",
  Boarding = "BOARDING",
  HouseCall = "HOUSE_CALL",
}

/** Matches CalendarResourceProviderSerializer */
type CalendarResourceProvider = {
  id: number;
  name: string;
  initials: string;
  image_url: string | null;
  user_id: number;
};

/** Matches scheduler CalendarResourceKind */
export enum CalendarResourceKind {
  Provider = "PROVIDER",
  Accommodation = "ACCOMMODATION",
}

/** Matches AccommodationKindCalendarResourceSerializer */
export type AccommodationKindCalendarResource = {
  id: number;
  name: string;
  rank: number;
};

/** Matches AccommodationKindSerializer */
export type AccommodationKind = {
  id: number;
  name: string;
  rank: number;
  allowed_species: PatientKind[];
  amenities: string[];
  min_weight: Optional<number>;
  max_weight: Optional<number>;
  calendar_resources: AccommodationKindCalendarResource[];
};

/** Matches CalendarResourceSerializer */
export type CalendarResource = {
  id: number;
  name: string;
  provider: Optional<CalendarResourceProvider>;
  accommodation_kind: Optional<AccommodationKind>;
  rank: number;
  can_double_book: boolean;
  can_client_double_book: boolean;
  icon: Optional<IconName>;
  is_imported: boolean;
  allowed_species: PatientKind[];
  kind: CalendarResourceKind;
};

/** matches scheduler TimeRangeData type */
type ResourceAvailabilityDateTime = {
  start_datetime: string;
  end_datetime: string;
  in_appropriate_designation: boolean;
};

/** Matches CalendarAvailabilityController.get_serialized_resource_data */
export type ResourceAvailabilityByResource = {
  resource: CalendarResource;
  availability_datetimes: ResourceAvailabilityDateTime[];
};

/** Matches CalendarAvailabilityController.get_serialized_resource_data_by_time */
export type ResourceAvailabilityByTime = {
  start_datetime: string;
  end_datetime: string;
  resources: CalendarResource[];
};
